import { useUserStore } from '@/stores';
import dayjs from 'dayjs';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const TheAuthContainer = (): Promise<typeof import('@/containers/TheAuthContainer.vue')> =>
  import('@/containers/TheAuthContainer.vue');
const Login = (): Promise<typeof import('@/views/auth/Login.vue')> => import('@/views/auth/Login.vue');
const TwoFactorVerification = (): Promise<typeof import('@/views/auth/TwoFactorVerification.vue')> =>
  import('@/views/auth/TwoFactorVerification.vue');
const ForgetPassword = (): Promise<typeof import('@/views/auth/ForgetPassword.vue')> =>
  import('@/views/auth/ForgetPassword.vue');
const ResetPassword = (): Promise<typeof import('@/views/auth/ResetPassword.vue')> =>
  import('@/views/auth/ResetPassword.vue');
const TheContainer = (): Promise<typeof import('@/containers/TheContainer.vue')> =>
  import('@/containers/TheContainer.vue');
const HomeContainer = (): Promise<typeof import('@/containers/HomeContainer.vue')> =>
  import('@/containers/HomeContainer.vue');
const Index = (): Promise<typeof import('@/views/Index.vue')> => import('@/views/Index.vue');
const QuestionStart = (): Promise<typeof import('@/views/QuestionStart.vue')> => import('@/views/QuestionStart.vue');
const QuestionStartPreview = (): Promise<typeof import('@/views/QuestionStartPreview.vue')> =>
  import('@/views/QuestionStartPreview.vue');
const QuestionItem = (): Promise<typeof import('@/views/QuestionItem.vue')> => import('@/views/QuestionItem.vue');
const QuestionItemByGroup = (): Promise<typeof import('@/views/QuestionItemByGroup.vue')> =>
  import('@/views/QuestionItemByGroup.vue');
const QuestionConfirm = (): Promise<typeof import('@/views/QuestionConfirm.vue')> =>
  import('@/views/QuestionConfirm.vue');
const QuestionComplete = (): Promise<typeof import('@/views/QuestionComplete.vue')> =>
  import('@/views/QuestionComplete.vue');
const QuestionCompletePreview = (): Promise<typeof import('@/views/QuestionCompletePreview.vue')> =>
  import('@/views/QuestionCompletePreview.vue');
const AnswerEdit = (): Promise<typeof import('@/views/AnswerEdit.vue')> =>
  import('@/views/AnswerEdit.vue');
const PasswordSetting = (): Promise<typeof import('@/views/auth/PasswordSetting.vue')> =>
  import('@/views/auth/PasswordSetting.vue');
const AutoSignIn = (): Promise<typeof import('@/views/auth/AutoSignIn.vue')> =>
  import('@/views/auth/AutoSignIn.vue');
const EclinroSignIn = (): Promise<typeof import('@/views/auth/EclinroSignIn.vue')> =>
  import('@/views/auth/EclinroSignIn.vue');
const PageNotFound = (): Promise<typeof import('@/views/PageNotFound.vue')> => import('@/views/PageNotFound.vue');
const SessionLogin = (): Promise<typeof import('@/components/SessionLogin.vue')> => import('@/components/SessionLogin.vue');
const EmailRegistration = (): Promise<typeof import('@/views/EmailRegistration.vue')> => import('@/views/EmailRegistration.vue');

const Home = (): Promise<typeof import('@/views/Home.vue')> =>
  import('@/views/Home.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth',
    component: TheAuthContainer,
    children: [
      // ログイン画面
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: '/',
        redirect: 'login',
      },
      {
        path: 'two-factor',
        name: 'TwoFactorVerification',
        component: TwoFactorVerification,
      },
      {
        path: 'password/send-email',
        name: 'ForgetPassword',
        component: ForgetPassword,
      },
      {
        path: 'password/reset',
        name: 'ResetPassword',
        component: ResetPassword,
      },
      {
        path: 'session-login',
        name: 'SessionLogin',
        component: SessionLogin,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    // プレビュー用
    path: '/preview',
    component: TheContainer,
    children: [
      // 各proの回答開始画面
      {
        path: ':uuid/start',
        name: 'QuestionStartPreview',
        component: QuestionStartPreview,
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire/:uuid/:question_group_no/:question_no',
        name: 'QuestionItemPreview',
        component: QuestionItem,
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire-by-group/:uuid/:question_group_no',
        name: 'QuestionItemByGroupPreview',
        component: QuestionItemByGroup,
      },
      // proごとの回答確認画面
      {
        path: 'questionnaire/:uuid/confirm',
        name: 'QuestionConfirmPreview',
        component: QuestionConfirm,
      },
      // proごとの回答完了画面
      {
        path: 'questionnaire/:uuid/complete',
        name: 'QuestionCompletePreview',
        component: QuestionCompletePreview,
      },
    ],
  },
  {
    // eClinRO用
    path: '/eclinro',
    component: TheContainer,
    children: [
      // eClinRO利用時のログイン処理
      {
        path: 'sign-in',
        name: 'EclinroSignIn',
        component: EclinroSignIn,
      },
      // 各proの回答開始画面
      {
        path: 'answer_date/:answer_date_id/start',
        name: 'QuestionStartEclinro',
        component: QuestionStart,
        meta: { requiresAuth: true },
      },
      // proごとの回答修正画面
      {
        path: 'answer_date/:answer_date_id/edit',
        name: 'AnswerEditEclinro',
        component: AnswerEdit,
        meta: { requiresAuth: true },
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire/:questionnaire_id/:question_group_no/:question_no',
        name: 'QuestionItemEclinro',
        component: QuestionItem,
        meta: { requiresAuth: true },
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire-by-group/:questionnaire_id/:question_group_no',
        name: 'QuestionItemByGroupEclinro',
        component: QuestionItemByGroup,
        meta: { requiresAuth: true },
      },
      // proごとの回答確認画面
      {
        path: 'questionnaire/:questionnaire_id/confirm',
        name: 'QuestionConfirmEclinro',
        component: QuestionConfirm,
        meta: { requiresAuth: true },
      },
      // proごとの回答完了画面
      {
        path: 'questionnaire/:questionnaire_id/complete',
        name: 'QuestionCompleteEclinro',
        component: QuestionComplete,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/',
    component: TheContainer,
    children: [
      // 日毎の質問紙がボタンで選択できる
      {
        path: '/index',
        name: 'Index',
        component: Index,
        meta: { requiresAuth: true },
      },
      {
        path: '/',
        redirect: '/home',
      },
      // 各proの回答開始画面
      {
        path: '/answer_date/:answer_date_id/start',
        name: 'QuestionStart',
        component: QuestionStart,
        meta: { requiresAuth: true },
      },
      // 各随時回答タイプproの回答開始画面
      {
        path: '/anytime/:questionnaire_id/start',
        name: 'AnytimeQuestionStart',
        component: QuestionStart,
        meta: { requiresAuth: true },
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: '/questionnaire/:questionnaire_id/:question_group_no/:question_no',
        name: 'QuestionItem',
        component: QuestionItem,
        meta: { requiresAuth: true },
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: '/questionnaire-by-group/:questionnaire_id/:question_group_no',
        name: 'QuestionItemByGroup',
        component: QuestionItemByGroup,
        meta: { requiresAuth: true },
      },
      // proごとの回答確認画面
      {
        path: '/questionnaire/:questionnaire_id/confirm',
        name: 'QuestionConfirm',
        component: QuestionConfirm,
        meta: { requiresAuth: true },
      },
      // proごとの回答完了画面
      {
        path: '/questionnaire/:questionnaire_id/complete',
        name: 'QuestionComplete',
        component: QuestionComplete,
        meta: { requiresAuth: true },
      },
      // proごとの回答修正画面
      {
        path: '/answer_date/:answer_date_id/edit',
        name: 'AnswerEdit',
        component: AnswerEdit,
        meta: { requiresAuth: true },
      },
      {
        path: '/password/setting',
        name: 'PasswordSetting',
        component: PasswordSetting,
        meta: { requiresAuth: true },
      },
      // リンクからAPIを実行させて自動ログインさせる
      {
        path: '/forms/:uuid',
        name: 'AutoSignIn',
        component: AutoSignIn,
      },
      // 404ページ
      {
        path: '/page-not-found',
        alias: '/:pathMatch(.*)*',
        name: 'PageNotFound',
        component: PageNotFound,
      },
    ],
  },
  {
    path: '/',
    component: HomeContainer,
    children: [
      {
        path: '/home',
        name: 'Home',
        component: Home,
        meta: { requiresAuth: true },
      },
      {
        path: '/email-registration',
        name: 'EmailRegistration',
        component: EmailRegistration,
        meta: { requiresAuth: true },
      },
    ]
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    return { left: 0, top: 0 };
  },
  routes,
});

// 認証を入れる
router.beforeEach((to, from, next) => {
  const store = useUserStore();
  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token == null
  ) {
    // 認証必要ページでtokenがなければログイン画面にリダイレクト
    next({ path: '/auth/login' });
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token != null &&
    !store.remember &&
    dayjs().unix() > store.limit
  ) {
    // 認証必要ページでtokenがあっても期限切れならログイン画面にリダイレクト
    store.$reset();
    next({ path: '/auth/login' });
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token != null &&
    !store.remember
  ) {
    // 認証必要ページでtokenが有効なら，期限を伸ばす
    const tokenLifetimeMinutes = parseInt(import.meta.env.VITE_APP_API_TOKEN_LIFETIME_MINUTES || '1440');
    const limit = dayjs().add(tokenLifetimeMinutes, 'minutes').unix();
    store.limit = limit;
    next();
  } else {
    // その他の場合はそのまま表示
    next();
  }
});

export default router;
